import React, { useState } from 'react';
import { 
 SpeakerWaveIcon, 
 HeartIcon, 
 SparklesIcon,
 CheckCircleIcon,
 PlayIcon,
 StopIcon
} from '@heroicons/react/24/outline';

const VoiceProfileSelector = ({ userId, onProfileSelect }) => {
 const [selectedProfile, setSelectedProfile] = useState('pleasant');
 const [isPlaying, setIsPlaying] = useState(false);
 const [audioRef, setAudioRef] = useState(null);

 const voiceProfiles = [
 {
 id: 'pleasant',
 name: 'Pleasant Voice',
 description: 'Smooth, soft, kind, and pleasant to listen to',
 icon: HeartIcon,
 color: 'from-pink-400 to-rose-500',
 features: [
 'Smooth transitions',
 'Soft and gentle tone',
 'Kind and warm sound',
 'Pleasant to listen to',
 'Reduced harshness'
 ],
 settings: {
 smoothing: true,
 softening: true,
 warmth_boost: 0.15,
 clarity_enhancement: 0.1,
 breath_control: true
 }
 },
 {
 id: 'professional',
 name: 'Professional Voice',
 description: 'Clear, confident, and authoritative',
 icon: SpeakerWaveIcon,
 color: 'from-blue-400 to-indigo-500',
 features: [
 'Clear pronunciation',
 'Confident delivery',
 'Professional tone',
 'High clarity',
 'Authoritative sound'
 ],
 settings: {
 clarity_enhancement: 0.2,
 noise_reduction: true,
 smoothing: true,
 warmth_boost: 0.05
 }
 },
 {
 id: 'friendly',
 name: 'Friendly Voice',
 description: 'Warm, approachable, and engaging',
 icon: SparklesIcon,
 color: 'from-yellow-400 to-orange-500',
 features: [
 'Warm and inviting',
 'Approachable tone',
 'Engaging delivery',
 'Friendly sound',
 'Natural flow'
 ],
 settings: {
 warmth_boost: 0.2,
 smoothing: true,
 softening: true,
 clarity_enhancement: 0.08
 }
 }
 ];

 const handleProfileSelect = (profile) => {
 setSelectedProfile(profile.id);
 if (onProfileSelect) {
 onProfileSelect(profile);
 }
 };

 const playSample = (profile) => {
 // In a real implementation, you would play a sample of the voice profile
 console.log(`Playing sample for ${profile.name}`);
 setIsPlaying(true);
 
 // Simulate playing
 setTimeout(() => {
 setIsPlaying(false);
 }, 3000);
 };

 return (
 <div className="bg-bgSection p-6 rounded-lg space-y-6">
 <div className="text-center">
 <h2 className="text-2xl font-bold text-white mb-2">Choose Your Voice Style</h2>
 <p className="text-muted">
 Select a voice profile that matches your content style and audience preferences.
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {voiceProfiles.map((profile) => {
 const IconComponent = profile.icon;
 const isSelected = selectedProfile === profile.id;
 
 return (
 <div
 key={profile.id}
 className={`relative bg-gray-700 rounded-lg p-6 cursor-pointer transition-all duration-200 ${
 isSelected 
 ? 'ring-2 ring-yellow-400 bg-gray-600' 
 : 'hover:bg-gray-600'
 }`}
 onClick={() => handleProfileSelect(profile)}
 >
 {/* Selection indicator */}
 {isSelected && (
 <div className="absolute top-4 right-4">
 <CheckCircleIcon className="h-6 w-6 text-primary" />
 </div>
 )}

 {/* Profile icon */}
 <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${profile.color} flex items-center justify-center mb-4 mx-auto`}>
 <IconComponent className="h-8 w-8 text-white" />
 </div>

 {/* Profile name */}
 <h3 className="text-xl font-bold text-white text-center mb-2">
 {profile.name}
 </h3>

 {/* Description */}
 <p className="text-muted text-center text-sm mb-4">
 {profile.description}
 </p>

 {/* Features */}
 <div className="space-y-2 mb-4">
 {profile.features.map((feature, index) => (
 <div key={index} className="flex items-center gap-2 text-sm">
 <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
 <span className="text-muted">{feature}</span>
 </div>
 ))}
 </div>

 {/* Play sample button */}
 <button
 onClick={(e) => {
 e.stopPropagation();
 playSample(profile);
 }}
 className="w-full flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-500 text-white py-2 px-4 rounded-lg transition-colors"
 >
 {isPlaying ? (
 <StopIcon className="h-4 w-4" />
 ) : (
 <PlayIcon className="h-4 w-4" />
 )}
 <span className="text-sm">
 {isPlaying ? 'Playing...' : 'Play Sample'}
 </span>
 </button>
 </div>
 );
 })}
 </div>

 {/* Selected profile details */}
 {selectedProfile && (
 <div className="bg-gray-700 p-4 rounded-lg">
 <h4 className="font-semibold text-white mb-2">Selected Profile Details</h4>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
 {Object.entries(voiceProfiles.find(p => p.id === selectedProfile)?.settings || {}).map(([key, value]) => (
 <div key={key} className="flex justify-between">
 <span className="text-muted capitalize">
 {key.replace('_', ' ')}:
 </span>
 <span className="text-white">
 {typeof value === 'boolean' ? (value ? '✓' : '✗') : value}
 </span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Enhancement info */}
 <div className="bg-gradient-to-r from-yellow-900 to-orange-900 border border-primary p-4 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <SparklesIcon className="h-5 w-5 text-primary" />
 <h4 className="font-semibold text-primary">Voice Enhancement Features</h4>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
 <div>
 <h5 className="font-medium text-primary mb-1">Audio Quality</h5>
 <ul className="text-primary space-y-1">
 <li>• Noise reduction</li>
 <li>• Smooth transitions</li>
 <li>• Clarity enhancement</li>
 </ul>
 </div>
 <div>
 <h5 className="font-medium text-primary mb-1">Voice Characteristics</h5>
 <ul className="text-primary space-y-1">
 <li>• Warmth boost</li>
 <li>• Softening effects</li>
 <li>• Breath control</li>
 </ul>
 </div>
 </div>
 </div>
 </div>
 );
};

export default VoiceProfileSelector;









